import * as React from 'react'
import { BrowserRouter, Switch, Route } from 'react-router-dom'
import Login from './containers/Login'
import Help from './containers/Help'
import KeycloakErrorPage from './containers/KeycloakErrorPage'
import type { AuthState } from './types/AuthState'
import type { Customization } from './types/Customization'
import SessionTimeout from './containers/SessionTimeout'
import LoggedOut from './containers/LoggedOut'
import McKinseyIDTheme from './themes'
import { ExpiredSessionError, verifyLoginSession } from './util/verifyLoginSession'

type AuthMappingProps = {
  jwt: string
  error: string
  authState?: AuthState
  isAuthenticated: boolean
  kcMessage: string
  customization?: Customization
  onSessionTimeout: () => void
  loggedOut: boolean
}

export const AuthMapping = ({
  jwt,
  error,
  authState,
  isAuthenticated,
  kcMessage,
  customization,
  onSessionTimeout,
  loggedOut,
}: AuthMappingProps): JSX.Element => {
  if (loggedOut) {
    return <LoggedOut />
  }
  if (isAuthenticated && authState) {
    if (Date.now() >= authState.exp * 1_000) {
      // JWT is expired
      return <SessionTimeout authState={authState} customization={customization} />
    }
    return (
      <Login
        jwt={jwt}
        error={error}
        authState={authState}
        kcMessage={kcMessage}
        onSessionTimeout={onSessionTimeout}
        customization={customization}
      />
    )
  }
  return <KeycloakErrorPage error={error} kcMessage={kcMessage} customization={customization} />
}

type RouterProps = {
  jwt: string
  error: string
  kcMessage: string
  loggedOut: boolean
}

/**
 * Result of verifying the login session token.
 * - `verifying`: signature verification is in-flight (JWKS fetch). We render
 *   nothing to avoid ever flashing the login form for an unverified token.
 * - `authenticated`: signature + issuer verified and the token is live.
 * - `expired`: signature + issuer verified but the token has expired.
 * - `invalid`: no token, malformed, untrusted issuer, or bad signature.
 */
type VerificationState =
  | { status: 'verifying' }
  | { status: 'authenticated'; authState: AuthState }
  | { status: 'expired'; authState: AuthState }
  | { status: 'invalid' }

const Router = ({ jwt, error, kcMessage, loggedOut }: RouterProps): JSX.Element => {
  const [verification, setVerification] = React.useState<VerificationState>({ status: 'verifying' })

  // Verify the login session token's signature against a trusted issuer before
  // treating it as authenticated. This is the security boundary: the browser no
  // longer trusts a merely-decodable token.
  React.useEffect(() => {
    let active = true
    setVerification({ status: 'verifying' })

    verifyLoginSession(jwt)
      .then((authState) => {
        if (active) setVerification({ status: 'authenticated', authState })
      })
      .catch((e) => {
        if (!active) return
        if (e instanceof ExpiredSessionError && e.authState) {
          setVerification({ status: 'expired', authState: e.authState })
        } else {
          setVerification({ status: 'invalid' })
        }
      })

    return (): void => {
      active = false
    }
  }, [jwt])

  const authState =
    verification.status === 'authenticated' || verification.status === 'expired' ? verification.authState : undefined
  const customization: Customization = authState?.customization || {}

  // In-session expiry (fired by Login's countdown): surface the timeout screen.
  const onSessionTimeout = (): void => {
    if (authState) setVerification({ status: 'expired', authState })
  }

  const renderMain = (): JSX.Element | null => {
    // `loggedOut` is a server-driven signal and needs no token.
    if (loggedOut) {
      return (
        <AuthMapping
          jwt={jwt}
          error={error}
          authState={undefined}
          isAuthenticated={false}
          kcMessage={kcMessage}
          customization={customization}
          onSessionTimeout={onSessionTimeout}
          loggedOut={loggedOut}
        />
      )
    }

    // Do not render anything (especially not the login form) until verification
    // has resolved.
    if (verification.status === 'verifying') {
      return null
    }

    const isAuthenticated = verification.status === 'authenticated' || verification.status === 'expired'

    return (
      <AuthMapping
        jwt={jwt}
        error={error}
        authState={authState}
        isAuthenticated={isAuthenticated}
        kcMessage={kcMessage}
        customization={customization}
        onSessionTimeout={onSessionTimeout}
        loggedOut={false}
      />
    )
  }

  return (
    <McKinseyIDTheme customization={customization}>
      <BrowserRouter>
        <Switch>
          <Route
            path="/help"
            render={(): JSX.Element => <Help authState={authState} customization={customization} />}
          />
          <Route render={renderMain} />
        </Switch>
      </BrowserRouter>
    </McKinseyIDTheme>
  )
}

export default Router
